import {
  IContentCard,
  IContentCardHeroBanner,
  IContentCardFeatures,
} from "@origin-digital/event-dispatcher";

import { IBrazeContentCard } from "../consts/interfaces";

export const convertRankToInt = (rank: string | undefined) => {
  const parsed = parseInt(rank || "0", 10);

  return isNaN(parsed) ? 0 : parsed;
};

// sort descending order
// missing or borked rank count as a zero
export const orderCards = (cards: IBrazeContentCard[]) => {
  const sortedCards = cards;
  sortedCards.sort((a: IBrazeContentCard, b: IBrazeContentCard) => {
    const numA = convertRankToInt(a.extras?.rank);
    const numB = convertRankToInt(b.extras?.rank);
    return numB - numA;
  });

  return sortedCards;
};

export const filterCards = (
  arr: IBrazeContentCard[],
  query: string,
  key: string
) => {
  if (arr?.length > 0) {
    return arr.filter((card) => {
      if (card?.extras) {
        return card.extras[key] === query;
      }
      return false;
    });
  } else {
    return [];
  }
};

// Handling the Control Group campaign cards and/or mis-configured cards
// ab-control-cards have:
// * isControl is true
// * title and description are both empty strings
// * extras are null
export const filterControlCards = (arr: IBrazeContentCard[]) => {
  return arr.filter((card) => {
    return (
      !card?.isControl || !!(card?.extras && (card?.title || card?.description))
    );
  });
};

export const getFilteredRankedCards = (
  cards: IBrazeContentCard[],
  location: string | undefined,
  limit: number | undefined
): IBrazeContentCard[] | [] => {
  if (!cards) {
    return [];
  }

  let filteredCards = cards;

  // check for ab-control-card
  filteredCards = filterControlCards(filteredCards);

  if (location) {
    // remove all cards in the wrong location
    filteredCards = filterCards(filteredCards, location, "location");
  }

  // reorder
  filteredCards = orderCards(filteredCards);

  if (limit) {
    // limit if set
    filteredCards = filteredCards.slice(0, limit);
  }

  return filteredCards;
};

// convert array of Braze cards to Content Cards
// Based off the KVP here:
// https://origindd.atlassian.net/wiki/spaces/KRAK/pages/2662629443/Braze+Content+Card+data+dictionary

// Note: Impression tracking tracking a single card at a time - need to confirm for multiple

export const translateBrazeCardsToContentCards = (
  cards: IBrazeContentCard[],
  logCardClick: (card: IBrazeContentCard, forContentCards?: boolean) => void,
  logCardDismissal: (card: IBrazeContentCard) => void,
  logCardImpressions: (
    card: IBrazeContentCard[],
    forContentCards?: boolean
  ) => void
): (IContentCard | IContentCardHeroBanner | IContentCardFeatures)[] =>
  cards.map((card) => {
    const { title, description, extras, linkText, pinned, url } = card;

    const {
      iconCloud,
      iconAlt,
      imageCloud,
      imageAlt,
      imageMobileCloud,
      imageMobileAlt,
      hasPostcode,
      ctaOutlinedLabel,
      ctaOutlinedAction,
      ctaLinkLabel,
      ctaLinkAction,
      ...nonMappedExtras
    } = extras;

    const logImpression = () => logCardImpressions([card], true);
    const logClick = () => logCardClick(card, true);
    const logClose = () => logCardDismissal(card);

    return {
      ...nonMappedExtras,
      title,
      disableClose: pinned,
      icon: iconCloud
        ? {
            cloudinaryPath: iconCloud,
            altText: iconAlt,
          }
        : undefined,
      image: imageCloud
        ? {
            cloudinaryPath: imageCloud,
            altText: imageAlt,
          }
        : undefined,
      imageMobile: imageMobileCloud
        ? {
            cloudinaryPath: imageMobileCloud,
            altText: imageMobileAlt,
          }
        : undefined,
      hasPostcode: hasPostcode ? !!hasPostcode : undefined,
      logClose,
      logImpression,
      body: description,
      ctaContained:
        linkText && url
          ? {
              label: linkText,
              action: url,
              logClick,
            }
          : undefined,
      ctaOutlined:
        ctaOutlinedLabel && ctaOutlinedAction
          ? {
              label: ctaOutlinedLabel,
              action: ctaOutlinedAction,
              logClick,
            }
          : undefined,
      ctaLink:
        ctaLinkLabel && ctaLinkAction
          ? {
              label: ctaLinkLabel,
              action: ctaLinkAction,
              logClick,
            }
          : undefined,
    } as IContentCard | IContentCardHeroBanner | IContentCardFeatures;
  });
